export const API_URL = process.env.NEXT_PUBLIC_API_URL || "https://api-dev.nitrosimracing.com.co";
const DEFAULT_TIMEOUT_MS = 12000;

export async function apiFetch(path, options = {}) {
  if (typeof window === "undefined" && process.env.NITRO_BUILD_OFFLINE === "1") {
    const error = new Error("Las solicitudes HTTP están deshabilitadas durante el build offline.");
    error.code = "NITRO_BUILD_OFFLINE";
    throw error;
  }

  const { timeoutMs = DEFAULT_TIMEOUT_MS, signal, ...fetchOptions } = options;
  const effectiveTimeoutMs = typeof timeoutMs === "number" && Number.isFinite(timeoutMs) && timeoutMs > 0
    ? timeoutMs
    : DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, effectiveTimeoutMs);
  const abortFromCaller = () => controller.abort(signal?.reason);

  if (signal) {
    if (signal.aborted) {
      abortFromCaller();
    } else {
      signal.addEventListener("abort", abortFromCaller, { once: true });
    }
  }

  let response;
  let data;
  try {
    // The private origin is read only on the server; browser/OAuth/socket origins stay public.
    const requestBaseUrl = serverRequestBaseUrl();
    response = await fetch(`${requestBaseUrl}${path}`, {
      ...fetchOptions,
      credentials: "include",
      signal: controller.signal,
      headers: normalizedHeaders(fetchOptions.headers, fetchOptions.body),
    });
    try {
      data = await response.json();
    } catch (error) {
      if (error?.name === "AbortError") throw error;
      if (response.ok) {
        const invalidResponse = new Error("Nitro devolvió una respuesta inválida.");
        invalidResponse.code = "NITRO_INVALID_RESPONSE";
        throw invalidResponse;
      }
      data = {};
    }
  } catch (error) {
    if (error.name === "AbortError" && timedOut) {
      throw new Error("La conexion con Nitro esta tardando demasiado. Intenta actualizar la pagina.");
    }
    throw error;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", abortFromCaller);
  }

  if (!response.ok) {
    const error = new Error(data?.error?.message || "No se pudo completar la solicitud.");
    error.details = data?.error?.details || null;
    error.code = data?.error?.code || data?.error?.details?.code || null;
    error.status = response.status;
    throw error;
  }

  return data;
}

function normalizedHeaders(input, body) {
  const headers = new Headers(input);
  // Bodyless GETs must remain simple CORS requests. FormData supplies its own boundary.
  if (!headers.has("Content-Type") && typeof body === "string") {
    try {
      JSON.parse(body);
      headers.set("Content-Type", "application/json");
    } catch {
      // Non-JSON bodies retain the browser's native content type.
    }
  }
  return headers;
}

function serverRequestBaseUrl() {
  if (typeof window !== "undefined" || !process.env.NITRO_INTERNAL_API_URL) return API_URL;
  let internal;
  try {
    internal = new URL(process.env.NITRO_INTERNAL_API_URL);
  } catch {
    throw new Error("El origen interno del API no es válido.");
  }
  if (!["http:", "https:"].includes(internal.protocol) || internal.username || internal.password || internal.search || internal.hash || internal.pathname !== "/") {
    throw new Error("El origen interno del API no es válido.");
  }
  return internal.origin;
}
